import { markdownLanguage } from "@codemirror/lang-markdown";
import { countColumn } from "@codemirror/state";
import type { SyntaxNode } from "@lezer/common";
import { diffArrays } from "diff";
import { parseMarkdownTables, serializeMarkdownTable, type TableChange } from "./markdown-table";

const TAB_SIZE = 4;
const FRONT_MATTER = /^---[ \t]*\n(?:[^\n]*\n)*?(?:---|\.\.\.)[ \t]*(?:\n|$)/;
const MAX_PASSES = 5;

function childNodes(node: SyntaxNode): SyntaxNode[] {
  const result: SyntaxNode[] = [];
  for (let child = node.firstChild; child; child = child.nextSibling) result.push(child);
  return result;
}

function contentChildren(node: SyntaxNode): SyntaxNode[] {
  return childNodes(node).filter((child) => child.name !== "QuoteMark" && child.name !== "ListMark");
}

/** Letters and digits outside anything shaped like an ordered list marker, which renumbering may change. */
function words(text: string): string {
  return text.replace(/(^|[\s>])\d{1,9}[.)](?=\s|$)/g, "$1").replace(/[^\p{L}\p{N}]/gu, "");
}

function trimEnd(line: string): string {
  return line.replace(/[ \t]+$/, "");
}

/** Remove up to `columns` columns of leading spaces and tabs. */
function stripColumns(text: string, columns: number): string {
  let column = 0;
  let index = 0;
  while (index < text.length && column < columns) {
    if (text[index] === " ") column += 1;
    else if (text[index] === "\t") column += TAB_SIZE - (column % TAB_SIZE);
    else break;
    index += 1;
  }
  return " ".repeat(Math.max(0, column - columns)) + text.slice(index);
}

/** Remove the whitespace prefix every non-blank line shares. */
function dedent(text: string): string {
  const lines = text.split("\n");
  const indents = lines.filter((line) => line.trim()).map((line) => /^[ \t]*/.exec(line)![0]);
  let common = indents[0] ?? "";
  for (const indent of indents) while (!indent.startsWith(common)) common = common.slice(0, -1);
  if (!common) return text;
  return lines.map((line) => line.startsWith(common) ? line.slice(common.length) : "").join("\n");
}

/** Prefer `_` for emphasis and `**` for strong emphasis when the change cannot alter the parse. */
function normalizeEmphasis(source: string): string {
  const text = source.split("");
  markdownLanguage.parser.parse(source).iterate({
    enter(node) {
      if (node.name !== "Emphasis" && node.name !== "StrongEmphasis") return;
      const marks = childNodes(node.node).filter((child) => child.name === "EmphasisMark");
      const open = marks[0];
      const close = marks[marks.length - 1];
      if (!open || !close || open.from === close.from) return;
      const mark = node.name === "Emphasis" ? "_" : "**";
      if (open.to - open.from !== mark.length || source.slice(open.from, open.to) === mark) return;
      const outside = (source[open.from - 1] ?? " ") + (source[close.to] ?? " ");
      const inside = source[open.to]! + source[close.from - 1]!;
      // Neighbouring delimiters could merge into a different run, and `_` cannot emphasise inside words.
      if (/[*_]/.test(outside + inside) || (mark === "_" && /[\p{L}\p{N}]/u.test(outside))) return;
      text.splice(open.from, mark.length, ...mark);
      text.splice(close.from, mark.length, ...mark);
    },
  });
  return text.join("");
}

/** Re-serialize every block with canonical indentation, markers, and spacing. */
function formatBlocks(source: string): string {
  const tree = markdownLanguage.parser.parse(source);
  const tables = new Map(parseMarkdownTables(source).map((table) => [table.from, table]));
  const lineStart = (position: number) => source.lastIndexOf("\n", position - 1) + 1;
  const lineEnd = (position: number) => {
    const end = source.indexOf("\n", position);
    return end < 0 ? source.length : end;
  };
  const quoteEnds = new Map<number, number[]>();
  tree.iterate({
    enter(node) {
      if (node.name !== "QuoteMark") return;
      const from = lineStart(node.from);
      quoteEnds.set(from, [...quoteEnds.get(from) ?? [], node.to]);
    },
  });
  const blankBetween = (from: number, to: number) => /\n[ \t>]*\n/.test(source.slice(from, to));
  // Where a line's text starts once its blockquote markers are skipped.
  const afterQuotes = (from: number, limit: number) => {
    const position = (quoteEnds.get(from) ?? []).filter((end) => end <= limit).pop() ?? from;
    return position > from && /[ \t]/.test(source[position] ?? "") ? position + 1 : position;
  };

  /** A block's lines without the blockquote markers and list indentation that contain it. */
  const blockLines = (node: SyntaxNode, to = node.to): string[] => {
    const base = afterQuotes(lineStart(node.from), node.from);
    const columns = countColumn(source.slice(base, node.from), TAB_SIZE);
    const lines = [source.slice(node.from, Math.min(lineEnd(node.from), to))];
    for (let from = lineEnd(node.from) + 1; from <= to; from = lineEnd(from) + 1) {
      const end = Math.min(lineEnd(from), to);
      lines.push(stripColumns(source.slice(afterQuotes(from, end), end), columns));
    }
    return lines;
  };

  const paragraph = (lines: string[]): string => lines.map((line, index) => {
    const text = line.replace(/^[ \t]+/, "").replace(/^(#{2,6})(?=[^\s#])/, "$1 ");
    const hardBreak = index < lines.length - 1 && / {2}$/.test(text);
    return trimEnd(text) + (hardBreak ? "  " : "");
  }).join("\n");

  const atxHeading = (node: SyntaxNode): string => {
    const marks = childNodes(node).filter((child) => child.name === "HeaderMark");
    const from = marks[0]?.to ?? node.from;
    const to = marks.length > 1 ? marks[marks.length - 1]!.from : node.to;
    const text = source.slice(from, to).trim();
    return "#".repeat(Number(node.name.slice(-1))) + (text ? ` ${text}` : "");
  };

  const setextHeading = (node: SyntaxNode): string => {
    const underline = childNodes(node).filter((child) => child.name === "HeaderMark").pop()!;
    const text = blockLines(node, lineStart(underline.from) - 1)
      .map((line) => line.trim()).filter(Boolean).join(" ");
    return `${node.name.endsWith("1") ? "#" : "##"} ${text}`;
  };

  const fencedCode = (node: SyntaxNode): string => {
    const lines = blockLines(node);
    const marks = childNodes(node).filter((child) => child.name === "CodeMark");
    const fence = source.slice(marks[0]!.from, marks[0]!.to);
    lines[0] = fence + lines[0]!.slice(fence.length).trim();
    if (marks.length > 1) {
      lines[lines.length - 1] = fence;
    } else {
      while (lines.length > 1 && !lines[lines.length - 1]!.trim()) lines.pop();
      lines.push(fence);
    }
    return lines.join("\n");
  };

  const table = (node: SyntaxNode): string => {
    const original = blockLines(node).map(trimEnd).join("\n");
    const parsed = tables.get(node.from);
    const formatted = parsed && serializeMarkdownTable({ ...parsed, prefix: "" });
    // Rows wider than the header would lose cells, so leave those tables as written.
    return formatted && words(formatted) === words(original) ? formatted : original;
  };

  const list = (lists: SyntaxNode[]): string => {
    const items = lists.flatMap((node) => childNodes(node).filter((child) => child.name === "ListItem"));
    const ordered = lists[0]!.name === "OrderedList";
    const firstMark = items[0]?.getChild("ListMark");
    const start = ordered && firstMark ? Number.parseInt(source.slice(firstMark.from, firstMark.to), 10) : 1;
    // An item's range can end with the quote marker of a following blank line, so measure from its content.
    const end = (item: SyntaxNode) => contentChildren(item).pop()?.to ?? item.getChild("ListMark")?.to ?? item.to;
    const loose = items.some((item, index) => {
      if (index > 0 && blankBetween(end(items[index - 1]!), item.from)) return true;
      const children = contentChildren(item);
      return children.some((child, i) => i > 0 && blankBetween(children[i - 1]!.to, child.from));
    });
    return items.map((item, index) => {
      const marker = ordered ? `${start + index}.` : "-";
      const indent = " ".repeat(marker.length + 1);
      return blocks(contentChildren(item), !loose).split("\n").map((line, i) => {
        if (i === 0) return line ? `${marker} ${line}` : marker;
        return line ? indent + line : "";
      }).join("\n");
    }).join(loose ? "\n\n" : "\n");
  };

  const block = (node: SyntaxNode): string => {
    if (node.name.startsWith("ATXHeading")) return atxHeading(node);
    if (node.name.startsWith("SetextHeading")) return setextHeading(node);
    switch (node.name) {
      case "Paragraph":
      case "Task":
        return paragraph(blockLines(node));
      case "Blockquote":
        return blocks(contentChildren(node)).split("\n").map((line) => line ? `> ${line}` : ">").join("\n");
      case "HorizontalRule":
        return "---";
      case "FencedCode":
        return fencedCode(node);
      // Indented code is almost always stray leading whitespace in a note, so read it as Markdown.
      case "CodeBlock":
        return formatPass(blockLines(node).join("\n"));
      case "Table":
        return table(node);
      default:
        return blockLines(node).map(trimEnd).join("\n");
    }
  };

  const blocks = (content: SyntaxNode[], tight = false): string => {
    const parts: string[] = [];
    for (let index = 0; index < content.length; index += 1) {
      const node = content[index]!;
      if (node.name === "BulletList" || node.name === "OrderedList") {
        // Lists that differ only by marker style become one list once markers are normalized.
        const lists = [node];
        while (content[index + 1]?.name === node.name) lists.push(content[++index]!);
        parts.push(list(lists));
      } else {
        parts.push(block(node));
      }
    }
    return parts.filter(Boolean).join(tight ? "\n" : "\n\n");
  };

  return blocks(contentChildren(tree.topNode));
}

function formatPass(source: string): string {
  return formatBlocks(normalizeEmphasis(source));
}

/**
 * Clean up a whole Markdown note: stray indentation, trailing spaces, blank lines,
 * headings, list markers and numbering, emphasis markers, and table alignment.
 * Fenced code and front matter keep their contents. Returns null when the result
 * would not keep every letter and digit of the original, so a formatting bug cannot drop text.
 */
export function formatMarkdown(source: string): string | null {
  const normalized = source.replace(/\r\n?/g, "\n");
  const frontMatter = FRONT_MATTER.exec(normalized)?.[0] ?? "";
  let body = dedent(normalized.slice(frontMatter.length));
  for (let pass = 0; pass < MAX_PASSES; pass += 1) {
    const next = formatPass(body);
    if (next === body) break;
    body = next;
  }
  const result = [frontMatter.trimEnd(), body].filter(Boolean).join("\n\n");
  const formatted = result ? `${result}\n` : "";
  return words(formatted) === words(normalized) ? formatted : null;
}

/** Narrow a replacement to the characters that differ, so the cursor stays put on a reformatted line. */
function narrowChange(from: number, removed: string, insert: string): TableChange | null {
  let start = 0;
  while (start < removed.length && start < insert.length && removed[start] === insert[start]) start += 1;
  let end = 0;
  while (end < removed.length - start && end < insert.length - start
    && removed[removed.length - 1 - end] === insert[insert.length - 1 - end]) end += 1;
  if (start + end === removed.length && start + end === insert.length) return null;
  return { from: from + start, to: from + removed.length - end, insert: insert.slice(start, insert.length - end) };
}

/** Small edits that turn `source` into its formatted form, or null when it cannot be formatted safely. */
export function markdownFormatChanges(source: string): TableChange[] | null {
  const formatted = formatMarkdown(source);
  if (formatted === null) return null;
  const lines = (text: string) => text.match(/[^\n]*\n|[^\n]+$/g) ?? [];
  const before = lines(source);
  const after = lines(formatted);
  // Pair lines that differ only by indentation, list or quote markers, or trailing spaces.
  const key = (line: string) => line.replace(/^[ \t>]*(?:(?:[-+*]|\d{1,9}[.)])[ \t]+)?/, "").trimEnd();
  const changes: TableChange[] = [];
  const push = (change: TableChange | null) => {
    if (change) changes.push(change);
  };
  let oldIndex = 0;
  let newIndex = 0;
  let offset = 0;
  let from = 0;
  let removed = "";
  let inserted = "";
  for (const part of diffArrays(before, after, { comparator: (left, right) => key(left) === key(right) })) {
    if (part.added || part.removed) {
      if (!removed && !inserted) from = offset;
      if (part.added) {
        inserted += after.slice(newIndex, newIndex + part.count).join("");
        newIndex += part.count;
      } else {
        const text = before.slice(oldIndex, oldIndex + part.count).join("");
        removed += text;
        offset += text.length;
        oldIndex += part.count;
      }
      continue;
    }
    push(narrowChange(from, removed, inserted));
    removed = inserted = "";
    for (let index = 0; index < part.count; index += 1) {
      const line = before[oldIndex + index]!;
      push(narrowChange(offset, line, after[newIndex + index]!));
      offset += line.length;
    }
    oldIndex += part.count;
    newIndex += part.count;
  }
  push(narrowChange(from, removed, inserted));
  return changes;
}
